import { useRouter } from "next/router";
import { type FunctionComponent } from "react";

import { ClientSideSuspense } from "shared/components/common/client-side-suspense";
import { section as sectionData } from "shared/data";
import { useCurrentPageType } from "shared/hooks";
import type { Edition } from "shared/lib/edition";
import { PageType } from "shared/lib/page-type";

import { useAppBarSetStickyScrollHeight } from "scmp-app/components/app-bar/hooks/sticky/app-bar-set-sticky-scroll-height";
import { DefaultHeaderSwapThreshold } from "scmp-app/components/header/consts";
import { useHeaderFeaturesProvider } from "scmp-app/components/header/features/hooks";
import { HeaderLogo } from "scmp-app/components/section/section-style/styles";

import { useSwapOnScrollEffect } from "./hooks";
import {
  Container,
  HeaderContainer,
  LeftFeatureContainer,
  RightFeatureContainer,
  StyleHeaderLogoContainer,
} from "./styles";
import type { HeaderConfiguration } from "./types";

export type Props = {
  className?: string;
  headerConfiguration?: HeaderConfiguration;
  initEdition?: Edition;
  isHomeMiniHeader?: boolean;
};

type HeaderState = "article" | "default" | "homeMiniHeader" | "style";

export const Header: FunctionComponent<Props> = ({
  className,
  headerConfiguration,
  initEdition,
  isHomeMiniHeader,
}) => {
  const { query } = useRouter();
  const currentPageType = useCurrentPageType();
  const { reference } = useAppBarSetStickyScrollHeight();

  const isMagazineStyle = headerConfiguration?.appBarVariant === "scmp/magazines-style";
  const isSectionStyleMainPage =
    currentPageType === PageType.Section && query.entityId === sectionData.style.entityId;
  const isArticlePage = currentPageType === PageType.Article;

  const { shouldSwap } = useSwapOnScrollEffect(
    (headerConfiguration?.appBarStickyScrollHeight ?? 0) || DefaultHeaderSwapThreshold,
  );

  const headerFeatureProvider = useHeaderFeaturesProvider();
  const currentHeaderState: HeaderState = (() => {
    if (isHomeMiniHeader) return "homeMiniHeader";
    if (isMagazineStyle) return "style";
    if (isArticlePage) {
      return "article";
    }
    return "default";
  })();

  const leftFeaturesMap: Record<HeaderState, React.ReactNode> = {
    article: (
      <>
        {headerFeatureProvider.hamburgerMenu({
          showIn: "mobileAndTablet",
        })}
        {headerFeatureProvider.logoLink({
          shouldSwap,
          showIn: "mobileUp",
          showOnSwap: "mobile",
        })}
        {headerFeatureProvider.search({
          showIn: "desktopUp",
        })}
        {headerFeatureProvider.myNews({ showIn: "tabletUp" })}
      </>
    ),
    default: (
      <>
        {headerFeatureProvider.hamburgerMenu({
          showIn: "mobileAndTablet",
        })}
        {headerFeatureProvider.logoLink({ shouldSwap, showIn: "mobileUp", showOnSwap: "mobile" })}
        {headerFeatureProvider.search({ showIn: "desktopUp" })}
        {headerFeatureProvider.myNews({ showIn: "tabletUp" })}
      </>
    ),
    homeMiniHeader: (
      <>
        {headerFeatureProvider.hamburgerMenu({ showIn: "mobileAndTablet" })}
        {headerFeatureProvider.logoLink({ showIn: "mobileUp" })}
        {headerFeatureProvider.search({ showIn: "desktopUp" })}
        {headerFeatureProvider.myNews({ showIn: "tabletUp" })}
      </>
    ),
    style: (
      <>
        {headerFeatureProvider.styleLogoLink({ showIn: "mobileUp" })}
        {headerFeatureProvider.styleTextLink({
          shouldSwap,
          showIn: "mobileUp",
          showOnSwap: "mobile",
        })}
      </>
    ),
  };
  const leftFeatures = (
    <ClientSideSuspense>{leftFeaturesMap[currentHeaderState]}</ClientSideSuspense>
  );

  const defaultCenterFeatures = headerFeatureProvider.oneLineMenu({
    appBarVariant: headerConfiguration?.appBarVariant,
    initEdition,
  });
  const centerFeaturesMap: Record<HeaderState, React.ReactNode> = {
    article: defaultCenterFeatures,
    default: (
      <>
        {headerFeatureProvider.logoTextLink({
          hideOnSwap: "mobile",
          shouldSwap,
          showIn: "mobileOnly",
          variant: "centered",
        })}
        {headerFeatureProvider.oneLineMenu({
          appBarVariant: headerConfiguration?.appBarVariant,
          initEdition,
        })}
      </>
    ),
    homeMiniHeader: defaultCenterFeatures,
    style: headerFeatureProvider.oneLineMenu({
      appBarVariant: headerConfiguration?.appBarVariant,
      initEdition,
    }),
  };
  const centerFeatures = centerFeaturesMap[currentHeaderState];

  const rightFeaturesMap: Record<HeaderState, React.ReactNode> = {
    article: (
      <>
        {headerFeatureProvider.subscription({
          isMagazineStyle,
          shouldSwap,
          showIn: "mobileUp",
          showOnSwap: "mobile",
        })}
        {headerFeatureProvider.share({ shouldSwap, showIn: "mobileOnly", showOnSwap: true })}
        {headerFeatureProvider.bookmark({ shouldSwap, showIn: "mobileOnly", showOnSwap: true })}
        {headerFeatureProvider.comment({ shouldSwap, showIn: "mobileOnly", showOnSwap: true })}
        {headerFeatureProvider.dashboard({
          hideOnSwap: "mobile",
          shouldSwap,
          showIn: "mobileUp",
        })}
      </>
    ),
    default: (
      <>
        {headerFeatureProvider.subscription({ showIn: "tabletUp" })}
        {headerFeatureProvider.dashboard({ showIn: "mobileUp" })}
      </>
    ),
    homeMiniHeader: (
      <>
        {headerFeatureProvider.subscription({ showIn: "tabletUp" })}
        {headerFeatureProvider.dashboard({ showIn: "mobileUp" })}
      </>
    ),
    style: (
      <>
        {headerFeatureProvider.subscription({ isMagazineStyle: true, showIn: "tabletUp" })}
        {headerFeatureProvider.search({ showIn: "desktopUp" })}
        {headerFeatureProvider.myNews({ showIn: "tabletUp" })}
        {headerFeatureProvider.dashboard({ showIn: "mobileUp" })}
        {headerFeatureProvider.hamburgerMenu({ showIn: "mobileAndTablet" })}
      </>
    ),
  };
  const rightFeatures = (
    <ClientSideSuspense>{rightFeaturesMap[currentHeaderState]}</ClientSideSuspense>
  );

  return (
    <Container $isMagazineStyle={isMagazineStyle} className={className}>
      {isSectionStyleMainPage && (
        <StyleHeaderLogoContainer ref={reference}>
          <HeaderLogo />
        </StyleHeaderLogoContainer>
      )}
      <HeaderContainer>
        <LeftFeatureContainer>{leftFeatures}</LeftFeatureContainer>
        {centerFeatures}
        <RightFeatureContainer>{rightFeatures}</RightFeatureContainer>
      </HeaderContainer>
    </Container>
  );
};

Header.displayName = "Header";
